import { Inject, Injectable, inject } from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { MyBetsVisualizationTrackingConfig } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { Odds, OddsFormatterService } from '@frontend/sports/odds/feature';
import { NOT_APPLICABLE, TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { SyncUserSettingsService, UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { flatten } from 'lodash-es';
import { SourceOfPick } from 'packages/sports/web/app/src/betslip-base/models/pick-models';
import { NumpadAction } from 'packages/sports/web/app/src/numpad/model';
import { NumpadTrackingService } from 'packages/sports/web/app/src/numpad/numpad-tracking.service';

import { IBetslipCommonMessages } from '../../../base/models/betslip-messages';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { SystemBankerSinglesSlipType, SystemSlipType } from '../../bet-generation/slip-type';
import { FinishPlaceBetPayload } from '../../betplacement/actions';
import { TokensRecord } from '../../reward-tokens/state';
import { ISystemBetPickState } from '../../system-bet/state';
import { IBetslipTypeState } from '../../types/state';
import { BetslipError } from '../../validation/errors/betslip-error';
import { BetslipTypePickErrors } from '../../validation/state';
import { BetslipTrackingConstants } from '../models';
import { BetSourceTracking, BetslipErrorTracking, BetslipTrackingData, TrackBetslipPick, TransactionAffiliate } from '../tracking.models';
import { BetslipTrackingHelper } from './betslip-tracking-helper';

@Injectable()
export abstract class BetslipBaseTrackingService {
    private messages: IBetslipCommonMessages;
    protected betSource: BetSourceTracking = {
        live: {},
        main: {},
    };

    protected oddsAcceptanceMessages: StringDictionary<string>[];
    private readonly syncUserSettingsService = inject(SyncUserSettingsService);
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        protected numpadTracking: NumpadTrackingService,
        protected trackingService: TrackingService,
        protected nativeService: NativeAppService,
        private oddsFormatterService: OddsFormatterService,
        protected mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) protected messageProvider: IBetslipMessagesProviderService,
    ) {
        this.messages = messageProvider.getMessages().common;

        this.oddsAcceptanceMessages = [
            { key: OddsAcceptanceMode.Any, value: this.messages.AcceptAny },
            { key: OddsAcceptanceMode.Higher, value: this.messages.AcceptHigher },
            { key: OddsAcceptanceMode.No, value: this.messages.AcceptNoChanges },
        ];
    }

    abstract trackAddRemovePick(eventName: string, pick: BetslipPick, state: IBetslipState, marketId?: string, groupId?: string): void;

    abstract trackBetPlacementResult(data: FinishPlaceBetPayload): void;

    trackBetPlacementErrors(typePickErrors: BetslipTypePickErrors, betslipError: BetslipError[], oddsAcceptance: OddsAcceptanceMode): void {
        const errors = Object.values(typePickErrors).flatMap((pickErrors) => Object.values(pickErrors).flatMap((e) => e));
        const betslipErrors = flatten(Object.values(betslipError));
        const tracks: string[] = [];
        if (errors.length || betslipErrors.length) {
            if (errors.length) {
                errors.forEach((e) => tracks.push(e.type));
            }
            if (betslipErrors.length) {
                tracks.push(betslipErrors[0].type);
            }

            tracks.forEach((track) => {
                this.trackingService.track(trackingConstants.EVENT_BETSLIP_ERROR, {
                    [trackingConstants.PAGE_NAME]: 'BetslipError_PushRunning_true',
                    [trackingConstants.PAGE_BETSLIP_ERROR]:
                        this.oddsAcceptanceMessages.find((item) => item.key === oddsAcceptance)?.value! + ' M2_slip_error_' + track,
                });
            });
        }
    }

    trackOpenSettings() {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_BSettingsLink' });
    }

    trackOddAcceptanceChange(acceptance: OddsAcceptanceMode) {
        switch (acceptance) {
            case OddsAcceptanceMode.Any:
                this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_AOdds' });
                break;
            case OddsAcceptanceMode.Higher:
                this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_HOdds' });
                break;
            case OddsAcceptanceMode.No:
                this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_NOdds' });
                break;
            default:
                break;
        }
    }

    trackEmailNotificationSettingChange(setting: boolean) {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: `M2_slip_action_option_EmailNotification/${setting}`,
        });
    }

    trackAppNotificationSettingChange(setting: boolean) {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: `M2_slip_action_option_AppNotification/${setting}`,
        });
    }

    trackBetslipClientErrors(trackingData: BetslipTrackingData) {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipErrorTracking.TrackingCategoryEvent,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipErrorTracking.TrackingLabelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipErrorTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: trackingData.positionEvent,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: BetslipErrorTracking.TrackingLocationEvent,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: trackingData.eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    getPickTracking(pick: BetslipPick, types: IBetslipTypeState): TrackBetslipPick {
        return {
            name: `${pick.tracking.source}${pick.tracking.sourceOfBetSuffix || ''}${pick.tracking.sheetviewSuffix ?? ''}`,
            category: BetslipTrackingHelper.getProductCategory(pick, null, undefined, types),
            price: this.getStake(types, pick.id.toString()),
            transactionId: generateGuid(),
            quantity: 1,
            sku: BetslipTrackingHelper.getSku(pick),
            transactionAffiliation: pick.isLive ? TransactionAffiliate.Livebook : TransactionAffiliate.Mainbook,
            [trackingConstants.PRODUCT_PICK_DETAILS]: BetslipTrackingHelper.getPickDetails(pick),
        } as TrackBetslipPick;
    }

    getTransactionProduct(
        pick: BetslipPick,
        typeState: IBetslipTypeState,
        betslipType: BetslipType | null,
        tokens: TokensRecord,
        pickCount: number,
        stake: number,
        betslipId?: string,
        groupOdds?: Odds,
    ): TrackBetslipPick {
        pick = this.sanitizeTracking(pick);
        const tracking = pick.tracking;

        const stakePrice = BetslipTrackingHelper.getStakePerPick(stake, pickCount, betslipType);

        const trackDetails = {
            name: `${BetslipTrackingHelper.getPickTrackingName(pick)}${tracking.numpadAction === NumpadAction.IncrementalStakeUsed ? '_QS' : ''}${pick.tracking.sheetviewSuffix ?? ''}`,
            category: BetslipTrackingHelper.getProductCategory(pick, betslipType, undefined, typeState),
            insurance: BetslipTrackingHelper.getRewardToken(tokens, typeState, betslipType, [pick], pickCount),
            quantity: 1,
            price: Math.round(stakePrice * 100),
            sku: BetslipTrackingHelper.getSku(pick),
        } as TrackBetslipPick;

        if (betslipId) {
            trackDetails.betslipID = betslipId;
        }

        if (typeState.base.currentSelectedType === BetslipType.System) {
            for (const pickId in typeState.systemBet.picks) {
                if (pick.id.isEqual(pickId)) {
                    trackDetails.systemBetType = this.systemBetName(typeState.systemBet.systemInfo, typeState.systemBet.picks);
                }
            }
        }

        trackDetails.transactionAffiliate = pick.isLive ? TransactionAffiliate.Livebook : TransactionAffiliate.Mainbook;

        if (pick.currentPrice?.nativeOdds && !pick.boostedPriceDetails?.isPriceBoosted) {
            trackDetails.odds = this.formatOdds(pick.currentPrice.nativeOdds);
        }

        if (pick.boostedPriceDetails && pick.boostedPriceDetails?.isPriceBoosted && pick.boostedPriceDetails.boostedPrice) {
            trackDetails.odds = this.formatOdds(pick.boostedPriceDetails.boostedPrice.nativeOdds);
        }

        if (groupOdds) {
            trackDetails.odds = this.formatOdds(groupOdds);
        }

        if (pick.tracking.gameDetails) {
            trackDetails[trackingConstants.GAME_DETAILS] = pick.tracking.gameDetails;
        }

        return trackDetails;
    }

    sanitizeTracking(pick: BetslipPick): BetslipPick {
        const regexp = new RegExp('W*' + SourceOfPick.Upsell + 'W*', 'i');
        pick.tracking.additional = regexp.test(pick.tracking.source) ? {} : pick.tracking.additional;

        return pick;
    }

    protected getOptionalPickTracking(pick: BetslipPick, marketId?: string): TrackBetslipPick {
        return {
            [trackingConstants.SPORT_ID]: pick.sportId || NOT_APPLICABLE,
            [trackingConstants.MARKET_ID]: marketId || pick.market.id?.toString() || NOT_APPLICABLE,
            [trackingConstants.RESULT_ID]: marketId || BetslipTrackingHelper.getResultId(pick) || NOT_APPLICABLE,
            [trackingConstants.SPORT_LEAGUE_ID]: BetslipTrackingHelper.getLeagueId(pick) || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: pick.tracking.additional
                ? (pick.tracking.additional[trackingConstants.COMPONENT_CONTENT_POSITION] ?? NOT_APPLICABLE)
                : NOT_APPLICABLE,
        } as TrackBetslipPick;
    }

    protected systemBetName(systemType: SystemSlipType | null, picks: { [pickId: string]: ISystemBetPickState }): string {
        if (!systemType) {
            return '';
        }

        const systemBetName = systemType?.key.toString();
        const bankerPicks = Object.values(picks).filter((pick) => pick.isSelected && pick.isBanker);
        const systemPicks = Object.values(picks).filter((pick) => pick.isSelected && !pick.isBanker);

        if (bankerPicks.length > 0) {
            return systemType instanceof SystemBankerSinglesSlipType
                ? bankerPicks.length + ' ' + this.messages.Bankers + ' + ' + systemPicks.length + ' ' + this.messages.Picks
                : bankerPicks.length + ' ' + this.messages.Bankers + ' - ' + systemBetName;
        }

        return systemBetName;
    }

    protected getStake(typeState: IBetslipTypeState, pickId?: string): number {
        switch (typeState.base.currentSelectedType) {
            case BetslipType.Single:
                if (pickId) {
                    return Number(typeState.singleBet.picks[pickId]?.stake || this.syncUserSettingsService.defaultStake || 0);
                }

                return Object.values(typeState.singleBet.picks).pop()?.stake || 0;
            case BetslipType.Combo:
                return Number(typeState.comboBet.stake);
            case BetslipType.System:
                return Number(typeState.systemBet.stake);
            case BetslipType.Teaser:
                return Number(typeState.teaserBet.stake);
            default:
                return this.syncUserSettingsService.defaultStake ?? 0;
        }
    }

    protected formatOdds(odds: Odds): string {
        return this.oddsFormatterService.format(odds, { displayFormat: this.userOddsFormatSettingsService.oddsFormat });
    }

    private getBetslipTrackingEventsInfo(trackingData: BetslipTrackingData): Partial<TrackData> {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipTrackingConstants.CategoryEvent,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipTrackingConstants.LabelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipTrackingConstants.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: trackingData.positionEvent,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: trackingData.isLinear
                ? BetslipTrackingConstants.SourceLinear
                : BetslipTrackingConstants.SourceTabbed,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipTrackingConstants.Close,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };
    }

    trackBetslipCTAClose(trackingData: BetslipTrackingData): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, this.getBetslipTrackingEventsInfo(trackingData));
    }
}
